import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TranslationKey } from '../../../../../../../../types/translation/TranslationKey';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import { t, translate } from '../../../../../../../../types/translation/Translator';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { Container } from '../../../../../../../../types/container';
import { LotContext } from '../../../../../../../../context/LotContext';
import CreateOrderCreateLotModal from '../../Modals/CreateOrderCreateLotModal';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { IconButton } from '../../../../../../../icons';
import { IconType } from '../../../../../../../icons/Icon';
import { Unilever } from '../../../../../../../../types/customer/DHL/Unilever/Unilever';
import { UserContext } from '../../../../../../../../context/UserContext';
import { System } from '../../../../../../../../types/system';
import { ProductMasterData } from '../../../../../../../../types/product_master_data';

interface ProductTransactionDestinationLotProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionDestinationLot({
  transaction,
  setTransaction,
  productMasterData,
  container,
  disabled = false,
}: ProductTransactionDestinationLotProps) {
  if (!productMasterData) return null;

  const { products } = useContext(ProductContext);
  const { lots } = useContext(LotContext);
  const { locale } = useContext(UserContext);

  const [createLotModalOpen, setCreateLotModalOpen] = useState<boolean>(false);

  const isDefined = useMemo(() => {
    if (transaction.product.fromProductId && transaction.containerTransactionId) return true;
    return false;
  }, []);

  if (!productMasterData.lotManaged) return null;
  if (transaction.type !== ProductTransactionType.inbound) return null;

  const validLots = useMemo(() => {
    return [...lots.values()]
      .filter(lot => {
        if (lot.companyId !== transaction.companyId) return false;
        if (transaction.product.pmdId && transaction.product.pmdId !== lot.productMasterDataId) return false;

        if (Unilever.companyIds.includes(transaction.companyId)) {
          let lotProducts = [...(products.get(transaction.product.pmdId || '')?.values() || [])].filter(
            p => p.lotId === lot.id,
          );
          if (!lotProducts.length) return false;

          if (transaction.product.toBinId) {
            lotProducts = lotProducts.filter(p => p.lotId === lot.id && p.binId === transaction.product.toBinId);
          }
          if (!lotProducts.length) return false;

          if (transaction.product.toContainerId) {
            lotProducts = lotProducts.filter(
              p => p.lotId === lot.id && p.containerId === transaction.product.toContainerId,
            );
          }
          if (!lotProducts.length) return false;
        }

        return true;
      })
      .sort((a, b) => {
        const aParsed = a.expirationDate ? Date.parse(a.expirationDate) : Number.NaN;
        const bParsed = b.expirationDate ? Date.parse(b.expirationDate) : Number.NaN;
        const aTimestamp = Number.isNaN(aParsed) ? Number.POSITIVE_INFINITY : aParsed;
        const bTimestamp = Number.isNaN(bParsed) ? Number.POSITIVE_INFINITY : bParsed;

        if (aTimestamp !== bTimestamp) return aTimestamp - bTimestamp;
        return a.number.localeCompare(b.number);
      });
  }, [lots, transaction, products]);

  useEffect(() => {
    if (Unilever.companyIds.includes(transaction.companyId)) {
      if (validLots.length === 1 && !transaction.product.lotId && transaction.product.toContainerId) {
        setTransaction(transaction => transaction.withLotId(validLots[0].id));
      }
    }
  }, [validLots]);

  return (
    <>
      <CreateOrderCreateLotModal
        open={createLotModalOpen}
        setOpen={setCreateLotModalOpen}
        onCreated={lot => setTransaction(transaction => transaction.withLotId(lot?.id))}
        pmdId={productMasterData.id}
      />
      <Grid item xs={6}>
        <div className='flex h-full items-center gap-1'>
          <div className='flex-grow'>
            <DropdownSearchSelect
              mandatory
              disabled={transaction.statusId !== System.ProductTransactionStatus.createdId || disabled || isDefined}
              placeholder={t(TranslationKey.lotNumber).singular.label}
              label={t(TranslationKey.lotNumber).singular.label}
              items={validLots}
              value={lots.get(transaction.product.lotId || '')}
              textBuilder={item => item.number}
              onSelect={v => setTransaction(transaction => transaction.withLotId(v?.id))}
              itemBuilder={item => (
                <div className='flex flex-col justify-center'>
                  <p>{item.number}</p>
                  <p className='text-xs text-gray-400'>
                    {item.expirationDate ? `${new Date(item.expirationDate)?.toLocaleDateString(locale)}` : ''}
                  </p>
                </div>
              )}
            />
          </div>
          {!transaction.product.toContainerId && !disabled ? (
            <div className='flex h-full items-center pt-6'>
              <IconButton
                className='border border-gray-default'
                type={IconType.plus}
                onClick={() => setCreateLotModalOpen(true)}
                text={translate({ key: TranslationKey.createNewLot })}
              />
            </div>
          ) : null}
        </div>
      </Grid>
    </>
  );
}
